import 'feather-icons'
import React, { useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import Header from '../components/Header'
import { useGlobalData } from '../contexts/GlobalData'
import { shouldFilterOutPair, usePairs, hardcodedIgnoreCryptoPairs } from '../contexts/PairData'
import { useDarkModeManager } from '../contexts/LocalStorage'
import { searchPairs } from '../utils/filter'
import LocalLoader from '../components/LocalLoader'
import { LoaderThin } from '../components/LoaderThin'
import { BasicLink } from '../components/Link'
import { formattedNum } from '../utils'
import { useHistory } from 'react-router-dom'
import DoubleTokenLogo from '../components/DoubleLogo'

const DarkPageContainer = styled.div`
  background: #16171c;
  min-height: 100vh;
  padding: 32px 48px 80px;
  margin-top: 40px;
  max-width: 1440px;
  margin-left: auto;
  margin-right: auto;
  width: 100%;

  @media screen and (max-width: 768px) {
    padding: 12px 20px 80px;
    margin-top: 40px;
  }
`

const BackButton = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  color: white;

  &:hover {
    opacity: 0.8;
  }
`

const HeaderNavigation = styled.div`
  display: flex;
  align-items: center;
  gap: 32px;
  margin-bottom: 48px;
`

const TabsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`

const TabButton = styled.button`
  background: ${(props) => (props.active ? '#222328' : 'transparent')};
  border: none;
  padding: 12px 16px;
  border-radius: 32px;
  color: white;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 15px;
  cursor: pointer;
  backdrop-filter: blur(20px);

  &:hover {
    opacity: 0.8;
  }
`

const HeaderSection = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 48px;
  width: 100%;
`

const TitleSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const MainTitle = styled.h1`
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 32px;
  color: white;
  margin: 0;
  line-height: 1;
`

const Description = styled.p`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 20px;
  color: rgba(255, 255, 255, 0.6);
  margin: 0;
  max-width: 756px;
  line-height: 1.5;
  letter-spacing: -0.2px;
`

const TVLSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 14px;
`

const TVLLabel = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  text-align: right;
  line-height: 1.4;
`

const TVLValue = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 700;
  font-size: 48px;
  color: white;
  line-height: 1;
  text-align: right;
`

const SearchContainer = styled.div`
  background: #202126;
  border: 1px solid #353840;
  border-radius: 16px;
  padding: 12px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
  height: 40px;
  margin-bottom: 48px;
`

const SearchInput = styled.input`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.5);
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  outline: none;
  width: 100%;

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`

const TableContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const TableWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const TableHeader = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 16px 0;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  border-bottom: 1px solid transparent;
`

const HeaderNameColumn = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 320px;
  flex-shrink: 0;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
`

const HeaderFlexColumn = styled.div`
  flex: 1;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
  line-height: 1.4;
`

const HeaderDetailsColumn = styled.div`
  width: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.5);
`

const TableRow = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  padding: 20px 0;
  border-top: 1px solid #222328;
  border-bottom: 1px solid #222328;
  margin-top: -1px;
`

const NameColumn = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  width: 320px;
  flex-shrink: 0;
`

const FlexColumn = styled.div`
  flex: 1;
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: white;
  line-height: 1.4;
`

const DetailsColumn = styled.div`
  width: 40px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  cursor: pointer;

  &:hover {
    opacity: 0.8;
  }
`

const TokenName = styled.div`
  font-family: 'Inter Display', sans-serif;
  font-weight: 500;
  font-size: 18px;
  color: white;
  line-height: 1.4;
`

const PriceChange = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;

  .positive {
    color: #45e560;
    font-family: 'Inter Display', sans-serif;
    font-weight: 600;
    font-size: 18px;
  }

  .negative {
    color: #ff8080;
    font-family: 'Inter Display', sans-serif;
    font-weight: 600;
    font-size: 18px;
  }
`

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 32px;

  @media screen and (max-width: 540px) {
    grid-template-columns: 1fr;
  }
`

export const PairTabs = {
  CRYPTO: 'Crypto',
  SECURITY: 'Security',
  ALL: 'All',
}

export const pairTabFilters = [PairTabs.SECURITY, PairTabs.CRYPTO]

function AllPoolsPage({ type = PairTabs.CRYPTO }) {
  const [searchValue, setSearchValue] = useState('')
  const history = useHistory()
  const [, toggleDarkMode] = useDarkModeManager()

  useEffect(() => {
    window.scrollTo(0, 0)
    // Force dark mode for this page
    toggleDarkMode(true)
  }, [toggleDarkMode])

  const onSearchChange = (e) => {
    const search = e.currentTarget.value
    setSearchValue(search)
    const normalizedSearch = search.trim()?.toLowerCase()
    if (normalizedSearch.length === 0) {
      setFilteredPairs({})
    }
    if (normalizedSearch?.length < 2) {
      return
    }
    const newPairs = searchPairs(usedPairs, normalizedSearch)
    if (newPairs) {
      setFilteredPairs(newPairs)
    }
  }

  const { secPairs = {}, cryptoPairs = {} } = usePairs()
  const usedPairs = useMemo(() => {
    if (type === PairTabs.SECURITY) {
      return secPairs
    }
    return cryptoPairs
  }, [cryptoPairs, secPairs, type])

  const [filteredPairs, setFilteredPairs] = useState({})

  const visiblePools = Object.keys(filteredPairs).length || searchValue.length ? filteredPairs : usedPairs
  const { totalValueLockedSecurity, totalValueLockedCrypto } = useGlobalData()
  const tvl = useMemo(() => {
    switch (type) {
      case PairTabs.SECURITY:
        return totalValueLockedSecurity
      default:
        return totalValueLockedCrypto
    }
  }, [type, totalValueLockedCrypto, totalValueLockedSecurity])
  return (
    <>
      <Header />
      <DarkPageContainer>
        <HeaderNavigation>
          <BackButton onClick={() => history.push('/home')}>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="18" viewBox="0 0 20 18" fill="none">
              <path
                opacity="0.5"
                d="M9 1L1 9M1 9L9 17M1 9H18.5"
                stroke="white"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </BackButton>

          <TabsContainer>
            <BasicLink to="/pools/security">
              <TabButton active={type === PairTabs.SECURITY}>Security Pools</TabButton>
            </BasicLink>
            <BasicLink to="/pools/crypto">
              <TabButton active={type === PairTabs.CRYPTO}>Crypto Pools</TabButton>
            </BasicLink>
          </TabsContainer>
        </HeaderNavigation>

        <HeaderSection>
          <TitleSection>
            <MainTitle>{type} Pools</MainTitle>
            <Description>
              {type === 'Crypto'
                ? 'Crypto Pools, also known as liquidity pools, are collective pools of digital assets held by participants in the cryptocurrency ecosystem. These pools enable individuals to contribute their cryptocurrencies or tokens to a shared liquidity pool, which in turn facilitates various decentralized financial activities.'
                : 'Security Pools are decentralized financial mechanisms in the realm of cryptocurrencies designed to enhance the security and stability of digital asset transactions. These pools serve as a collective resource where participants contribute their funds to mitigate risks associated with various crypto-related activities.'}
            </Description>
          </TitleSection>
          <TVLSection>
            <TVLLabel>Total Value locked</TVLLabel>
            {visiblePools && Object.keys(visiblePools)?.length !== 0 && <TVLValue>{formattedNum(tvl, true)}</TVLValue>}
            {(!visiblePools || Object.keys(visiblePools)?.length === 0) && <LoaderThin size={32} />}
          </TVLSection>
        </HeaderSection>

        <SearchContainer>
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
            <path
              d="M7 13C10.3137 13 13 10.3137 13 7C13 3.68629 10.3137 1 7 1C3.68629 1 1 3.68629 1 7C1 10.3137 3.68629 13 7 13Z"
              stroke="rgba(255, 255, 255, 0.5)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M15 15L11.5 11.5"
              stroke="rgba(255, 255, 255, 0.5)"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <SearchInput value={searchValue} placeholder="Search" onChange={onSearchChange} />
        </SearchContainer>

        {visiblePools && Object.keys(visiblePools)?.length !== 0 && (
          <TableContainer>
            <TableWrapper>
              <TableHeader>
                <HeaderNameColumn>Name</HeaderNameColumn>
                <HeaderFlexColumn>Volume</HeaderFlexColumn>
                <HeaderFlexColumn>Price Change</HeaderFlexColumn>
                <HeaderFlexColumn>Volume (24hrs)</HeaderFlexColumn>
                <HeaderFlexColumn>TVL</HeaderFlexColumn>
                <HeaderDetailsColumn>Details</HeaderDetailsColumn>
              </TableHeader>

              {Object.keys(visiblePools)
                .filter((poolId) => {
                  const pool = visiblePools[poolId]
                  if (!pool || shouldFilterOutPair(pool)) {
                    return false
                  }
                  if (type === PairTabs.CRYPTO) {
                    const token0Address = pool?.token0?.id?.toLowerCase()
                    const token1Address = pool?.token1?.id?.toLowerCase()
                    const token0Symbol = pool?.token0?.symbol
                    const token1Symbol = pool?.token1?.symbol
                    const shouldIgnoreToken =
                      hardcodedIgnoreCryptoPairs.includes(token0Address) ||
                      hardcodedIgnoreCryptoPairs.includes(token1Address) ||
                      token0Symbol === 'wTAU' ||
                      token1Symbol === 'wTAU' ||
                      token0Symbol === 'wMSTO' ||
                      token1Symbol === 'wMSTO'
                    return !shouldIgnoreToken
                  }
                  return true
                })
                .slice(0, 5)
                .map((poolId) => {
                  const pool = visiblePools[poolId]
                  const priceChangePercent = pool?.oneDayVolumeChangeUSD ? parseFloat(pool.oneDayVolumeChangeUSD) : 0
                  const isPositive = priceChangePercent >= 0

                  return (
                    <TableRow key={poolId}>
                      <NameColumn>
                        <DoubleTokenLogo a0={pool?.token0?.id} a1={pool?.token1?.id} size={24} margin={true} />
                        <TokenName>
                          {pool?.token0?.symbol}/{pool?.token1?.symbol}
                        </TokenName>
                      </NameColumn>
                      <FlexColumn>{formattedNum(pool?.volumeUSD, true)}</FlexColumn>
                      <FlexColumn>
                        <PriceChange>
                          <span className={isPositive ? 'positive' : 'negative'}>
                            {isPositive ? '+' : ''}
                            {priceChangePercent.toFixed(2)}%
                          </span>
                          <svg width="5" height="3" viewBox="0 0 5 3" fill="none">
                            <path
                              d="M0.5 2.5L2.5 0.5L4.5 2.5"
                              stroke={isPositive ? '#45e560' : '#ff8080'}
                              strokeWidth="1"
                            />
                          </svg>
                        </PriceChange>
                      </FlexColumn>
                      <FlexColumn>{formattedNum(pool?.oneDayVolumeUSD, true)}</FlexColumn>
                      <FlexColumn>{formattedNum(pool?.trackedReserveUSD, true)}</FlexColumn>
                      <DetailsColumn onClick={() => history.push(`/pools/${poolId}`)}>
                        <svg width="20" height="21" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <g opacity="0.5">
                            <path
                              d="M2.5 11.3333C5.5 4.66665 14.5 4.66665 17.5 11.3333"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                            <path
                              d="M10 14.6667C8.61925 14.6667 7.5 13.5474 7.5 12.1667C7.5 10.7859 8.61925 9.66669 10 9.66669C11.3807 9.66669 12.5 10.7859 12.5 12.1667C12.5 13.5474 11.3807 14.6667 10 14.6667Z"
                              stroke="white"
                              strokeWidth="1.5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </g>
                        </svg>
                      </DetailsColumn>
                    </TableRow>
                  )
                })}
            </TableWrapper>
          </TableContainer>
        )}

        {(!visiblePools || Object.keys(visiblePools)?.length === 0) && <LocalLoader />}
      </DarkPageContainer>
    </>
  )
}

export default AllPoolsPage
